import type { View } from "@novorender/api";
import type { HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";
import type { Predicate } from "@procore/bim-index";

import {
    type BoundsIds,
    type BoundsOptions,
    byProperty,
    type CollapsedIds,
    type FindIdsOptions,
    type FindOptions,
    type ObjectDataAccess,
    type ObjectVisibilityStats,
    type ObjectVisibilityStatsRequest,
    type PropertyQuery,
    resolveSelectionLegacy,
    type SelectionBounds,
    type SelectionResolutionMode,
    type SelectionResolutionOptions,
    type SelectionTarget,
    singleSelectionTarget,
} from "dataAccess";
import { PUBLIC_ID_PROPERTY } from "dataAccess/constants";
import { NodeType } from "types/misc";
import { log } from "utils/log";
import { isLeafNode } from "utils/objectPath";
import type { SelectionSet } from "utils/selectionSet";

import { DescendantCache } from "./descendantCache";
import { isInstanceId, isInternalObjectId, translateIds } from "./idTranslation";
import {
    ancestorPaths,
    childrenPredicate,
    objectNodeClassPredicate,
    pathsPredicate,
    queryPredicate,
} from "./predicate";
import type { Requests } from "./protocol";
import { bimIndexClient, closeBimIndex, ensureBimIndexOpen } from "./session";
import {
    fileReference,
    readObjectVisibilityStats,
    type VisibilityStatsFile,
    type VisibilityStatsIndex,
} from "./visibilityStats";

/** How many tree rows to pull per worker call while an iterator is consumed. */
const PAGE_SIZE = 200;

/** One row of a `matchRows` page — what every listing on this backend is built from. */
type MatchRow = Requests["matchRows"]["result"]["items"][number];

const EMPTY_ID_ARRAY = new Uint32Array();

/**
 * The two resolution modes as the engine's climb policies: the shallowest matching
 * ancestor is the "first object", the deepest is the "last".
 */
const CLIMB_POLICIES: Record<SelectionResolutionMode, Requests["resolveSelection"]["params"]["policy"]> = {
    "first-object": "topmost",
    "last-object": "nearest",
};

async function* emptyIterator(): AsyncIterableIterator<HierarcicalObjectReference> {}

/**
 * Data access backed by a BIM index **already downloaded to this device**.
 *
 * *Offline* is the whole point of it, and is why it is in the name: the index files
 * sit in OPFS, the WASM engine reads them through sync access handles in a worker,
 * and every query — hierarchy, properties, search — is answered in-process with no
 * request per node and no network at all. It is chosen only for a scene that has the
 * files (see `availability.ts`), and a scene that does not falls back to the object
 * DB rather than fetching them.
 *
 * A *served* BIM index is the sibling to expect: same index, same query algebra, but
 * queried over the network instead, and so available to every scene rather than only
 * a downloaded one. It is a separate implementation because the two differ in exactly
 * what the interface asks about — `isIndexAvailableOffline` is unconditionally `true`
 * here and would be `false` there — not because they answer differently. The hierarchy
 * reads are the same queries on both, down to the predicate: `$path` and `$depth` say
 * where a node sits without either side naming the ids its own engine minted (see
 * {@link getChildren}, {@link getAncestors}).
 *
 * Object *properties* are the one thing the index cannot provide: it is an inverted
 * index with no forward per-object record, and the native reader that has one seeks
 * into the original source document, which the browser build cannot open. Those come
 * from the scene's own metadata (`view.data`) instead, which keeps this backend free
 * of the network — and answers `undefined` for objects when a scene carries no
 * metadata, rather than reaching for a server. Object *geometry* is not in that
 * position: it has its own section (`bounds.idx`), so a row carries the bounds a fly-to
 * frames without the scene having to know the object at all.
 *
 * The index loads on the first call that needs it rather than at construction, so
 * choosing this implementation at startup costs nothing until it is asked something.
 * `openParams` names the OPFS folder the index files sit in — the worker only ever
 * reads what someone else has already downloaded there.
 */
export class BimIndexOfflineDataAccess implements ObjectDataAccess {
    readonly kind = "bim-index-offline" as const;

    /**
     * Subtree ids for this session, and what `object.descendants` reads through. Held
     * here rather than as a `WeakMap` keyed by the backend because there is exactly one
     * backend per scene, so the cache's lifetime *is* the scene's.
     */
    private readonly descendants = new DescendantCache();

    constructor(
        private readonly view: View,
        private readonly openParams: Requests["open"]["params"],
    ) {}

    isIndexAvailableOffline() {
        return true;
    }

    isDataAvailableOffline() {
        return Boolean(this.view.data);
    }

    /**
     * Straight out of the scene's metadata. A `MetaDataEntry` is an `ObjectData`
     * without its `loadMetaData`/`save` methods, which no consumer of this call uses —
     * the same cast `utils/search.getObjectData` makes on this path.
     */
    async getObject(id: ObjectId): Promise<ObjectData | undefined> {
        try {
            return (await this.view.data?.getObjectMetaData(id)) as ObjectData | undefined;
        } catch {
            // A missing or unreadable entry is "no object", as on the db path.
            return undefined;
        }
    }

    /**
     * The index resolves the path locally (`$path` is hash-indexed, so this is an
     * equality lookup rather than a scan); the object it names comes from the scene
     * metadata, since the index holds no properties.
     */
    async getObjectAtPath(path: string, signal?: AbortSignal): Promise<ObjectData | undefined> {
        await this.open();
        if (signal?.aborted) {
            return undefined;
        }
        const id = await bimIndexClient().call("objectIdAtPath", { path });
        return id === null ? undefined : this.getObject(id);
    }

    /**
     * By id, straight off the index's parent map — no path arithmetic and no request.
     * The parent object itself comes from the scene metadata.
     *
     * Stays on the parent map where {@link getAncestors} does not: one `climb` answers
     * the one id this wants, and the object behind it is a metadata read either way.
     */
    async getParent(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<ObjectData | undefined> {
        await this.open();
        if (signal?.aborted) {
            return undefined;
        }
        const parentId = await bimIndexClient().call("parentId", { id: object.id });
        return parentId === null ? undefined : this.getObject(parentId);
    }

    /**
     * A node's children, or the top-level nodes when given none — as a query over the
     * node's own path, not the engine's `getModelTree`.
     *
     * A query because a row needs more than a tree node answers: the tree draws a
     * **path** and reads **bounds** off the row for its own fly-to, and `getModelTree`
     * carries neither — a path only because the worker resolves one per node, and
     * geometry not at all. A match row carries both (see the protocol's `matchRows`), so
     * this is the same trade the served backend makes, and {@link childrenPredicate} is
     * literally the same predicate.
     *
     * Rows arrive in id order rather than the tree's, which is the caller's to sort — the
     * model tree already orders siblings by path. Paged as the iterator is consumed, so a
     * caller that stops early stops the work.
     */
    getChildren(
        parent: HierarcicalObjectReference | undefined,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        return this.matches(childrenPredicate(parent?.path), signal);
    }

    /**
     * One predicate over the query's terms, paged as the iterator is consumed.
     *
     * {@link FindOptions.full} asks for nothing extra here and so is not read: the index
     * holds no properties, so every row's metadata comes from the scene either way — a
     * local read per row, which is what `full` exists to avoid *paying over the network*.
     * There is no wider page to ask the engine for.
     */
    findByProperties(query: PropertyQuery, options?: FindOptions): AsyncIterableIterator<HierarcicalObjectReference> {
        const predicate = queryPredicate(query);
        return predicate ? this.matches(predicate, options?.signal) : emptyIterator();
    }

    /**
     * One worker call: the engine's `expand` is the same `ExpandAsync` primitive the
     * served backend reaches over `objects/expand`, and the match bitmap feeds it
     * without leaving WASM — so no rows are built and no intermediate ids materialized.
     *
     * Nothing here reads a node type. `expand` unions each root's *self-inclusive*
     * subtree, so a leaf contributes itself and the matches need no merging back in.
     */
    async findIdsByProperties(query: PropertyQuery, options?: FindIdsOptions): Promise<Uint32Array> {
        const { includeDescendants = false, requireWhole, signal } = options ?? {};
        const predicate = queryPredicate(query);
        if (!predicate) {
            return new Uint32Array();
        }

        await this.open();
        if (signal?.aborted) {
            return new Uint32Array();
        }

        const { ids, incomplete } = await bimIndexClient().call("matchIds", { predicate, includeDescendants });
        if (incomplete && !signal?.aborted && requireWhole) {
            throw new Error("the engine could not finish the scan");
        }
        if (incomplete) {
            log.dataAccess.bimIndex.warn("the engine could not finish the scan; ids may be incomplete");
        }
        return signal?.aborted ? new Uint32Array() : ids;
    }

    /**
     * The index's own id mapping (`id_mappings.idx`), read in the worker — so this is the
     * one backend that answers it with no network at all.
     *
     * `undefined` for an index built without that section: the mapping is simply not there,
     * and the caller's fallback finds the ids by property instead, which is a different
     * section of the same index. Not the same answer as an array of `null`s, which would
     * claim these objects have no instance id.
     */
    resolveObjectIdsToInstanceIds(objectIds: ObjectId[], signal?: AbortSignal): Promise<(string | null)[] | undefined> {
        return translateIds(objectIds, isInternalObjectId, async (readable) => {
            await this.open();
            if (signal?.aborted) {
                return undefined;
            }
            // Transferred rather than copied: this is one array per call, and it is built
            // here — `Uint32Array` because that is what the engine's bitmap takes, which is
            // also why the ids are vetted first (`idTranslation`).
            const ids = Uint32Array.from(readable);
            return (await bimIndexClient().call("externalIdsForObjects", { ids }, [ids.buffer])) ?? undefined;
        });
    }

    /** The same mapping the other way round — see {@link resolveObjectIdsToInstanceIds}. */
    resolveInstanceIdsToObjectIds(
        instanceIds: string[],
        signal?: AbortSignal,
    ): Promise<(ObjectId | null)[] | undefined> {
        return translateIds(instanceIds, isInstanceId, async (readable) => {
            await this.open();
            if (signal?.aborted) {
                return undefined;
            }
            return (await bimIndexClient().call("objectIdsForExternals", { externalIds: readable })) ?? undefined;
        });
    }

    /**
     * The engine's own `collapse`. `ids` is copied rather than transferred: it is the
     * caller's array. A failure other than the abort, or an incomplete answer, answers
     * every id as an object.
     */
    async collapseIds(ids: Uint32Array, signal?: AbortSignal): Promise<CollapsedIds> {
        const empty = { subtreeIds: EMPTY_ID_ARRAY, objectIds: EMPTY_ID_ARRAY };
        if (!ids.length) {
            return empty;
        }

        try {
            await this.open();
            if (signal?.aborted) {
                return empty;
            }
            const { subtreeIds, objectIds, incomplete } = await bimIndexClient().call("collapse", { ids });
            if (signal?.aborted) {
                return empty;
            }
            if (incomplete) {
                log.dataAccess.bimIndex.warn("incomplete collapse; keeping every id as an object");
                return { subtreeIds: EMPTY_ID_ARRAY, objectIds: ids };
            }
            return { subtreeIds, objectIds };
        } catch (e) {
            if (signal?.aborted) {
                return empty;
            }
            log.dataAccess.bimIndex.warn("collapse failed; keeping every id as an object", e);
            return { subtreeIds: EMPTY_ID_ARRAY, objectIds: ids };
        }
    }

    /** Tree rows for a predicate, paged as the iterator is consumed. */
    private async *matches(
        predicate: Predicate,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        await this.open();
        for (let offset = 0; !signal?.aborted; offset += PAGE_SIZE) {
            const page: Requests["matchRows"]["result"] = await bimIndexClient().call("matchRows", {
                predicate,
                offset,
                limit: PAGE_SIZE,
            });
            for (const row of page.items) {
                yield this.reference(row);
            }
            if (!page.items.length || offset + page.items.length >= page.total) {
                return;
            }
        }
    }

    /**
     * The chain root-first, as one query over the pieces of the object's own path — not
     * the parent-map walk.
     *
     * A query for {@link getChildren}'s reason, and one more: the walk is a `climb` per
     * level, where the prefixes of a path *are* its ancestors' paths
     * ({@link ancestorPaths}), so one `$path` lookup answers every level at once — and
     * answers with rows that carry the bounds a breadcrumb's fly-to needs.
     * {@link getParent} stays on the parent map, since all it wants is an id.
     *
     * The order is imposed here rather than taken from the engine: rows come back in id
     * order, and each is placed by the prefix that asked for it, which is already
     * root-first. A prefix no object sits at is left out rather than guessed — the gap the
     * interface tells callers to cope with, and one this backend cannot have on an index
     * built under the "every level owns an object" contract.
     */
    async getAncestors(
        object: HierarcicalObjectReference,
        signal?: AbortSignal,
    ): Promise<HierarcicalObjectReference[]> {
        const paths = ancestorPaths(object.path);
        const predicate = pathsPredicate(paths);
        if (!predicate) {
            return [];
        }
        const rows = new Map<string, HierarcicalObjectReference>();
        for await (const row of this.matches(predicate, signal)) {
            rows.set(row.path, row);
        }
        if (signal?.aborted) {
            // A chain cut short is not a shorter chain: the interface answers an abort
            // with its empty value.
            return [];
        }
        return paths.map((path) => rows.get(path)).filter((row): row is HierarcicalObjectReference => Boolean(row));
    }

    /**
     * One bitmap out of the index's object tree, materialized as ids and transferred from
     * the worker rather than copied — then kept in {@link DescendantCache} and read back
     * off the object through it.
     *
     * `object.descendants` becomes an accessor over the cache rather than the ids
     * themselves: `treeHelpers` counts them against the selection *synchronously* to
     * render a collapsed folder's checkbox, which no amount of "the files are local"
     * makes reachable — a worker call cannot answer a getter. Assigning the expanded
     * array instead would pin up to MBs per folder to a reference the tree recreates
     * constantly, with nothing bounding the total; the cache's two tiers are bounded.
     *
     * Cached here rather than in {@link warmDescendantIds} because a read made for *any*
     * reason — a user hiding a folder, selection resolution climbing a subtree, a
     * background warm-up — then serves every later one.
     *
     * The ids are ascending, since a bitmap decodes in order, which is what
     * `countSelectedDescendants`' binary search needs.
     */
    async getDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<Uint32Array> {
        if (signal?.aborted) {
            return EMPTY_ID_ARRAY;
        }
        const own = object.descendants;
        if (own) {
            return own instanceof Uint32Array ? own : new Uint32Array(own);
        }
        // `isLeafNode` rather than the node type: a container file is often stamped
        // `Leaf` and still owns a subtree (BM-10439), and the objects reaching here can
        // come from scene metadata rather than from a row this backend typed itself.
        if (isLeafNode(object)) {
            return EMPTY_ID_ARRAY;
        }

        await this.open();
        if (signal?.aborted) {
            return EMPTY_ID_ARRAY;
        }
        const ids = await this.descendants.getOrLoad(object, signal, async () =>
            // The shared signal goes unread: a worker call cannot be cancelled, so by the
            // time an abandoned read could be noticed the ids are in hand and worth
            // keeping. The engine answers a whole subtree or fails, never a short one.
            ({ ids: await bimIndexClient().call("descendantIds", { id: object.id }), complete: true }),
        );
        if (signal?.aborted) {
            return EMPTY_ID_ARRAY;
        }
        this.descendants.defineLazyDescendants(object);
        return ids;
    }

    /**
     * A {@link getDescendantIds} read for its side effect — the ids on the object — and
     * nothing else. Which node still needs one is the caller's business, but asking twice
     * costs nothing: a node that already has them is answered without a worker call.
     *
     * Cheaper than the served backend's warm-up, which spends a request per node, but not
     * free: the subtree is read out of OPFS and the shrunk set stays in memory for as long
     * as the cache keeps it. Callers bound the run either way.
     *
     * Reports whether the ids are now on the object, which is the only thing a caller can
     * act on. So a `false` is not "no descendants" — an empty subtree is warmed and reports
     * `true`. It means the read was aborted or failed, which is left to the next attempt
     * rather than failing the batch around it.
     */
    async warmDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<boolean> {
        try {
            await this.getDescendantIds(object, signal);
        } catch (e) {
            log.dataAccess.bimIndex.warn(`could not warm the subtree of object ${object.id}`, e);
            return false;
        }
        return Boolean(object.descendants);
    }

    /**
     * One worker call: the engine folds the volume off its bounds table for the whole
     * match set, so a selection of any size costs the same as one object and
     * nothing but the answer crosses back — the served backend reaches the very same
     * primitive over `objects/bounds`.
     *
     * `internalIds` become an `$id IN` term and `publicIds` a `Procore/InstanceId IN` one
     * (see {@link ObjectDataAccess.getBounds}); `predicate.ts` already chunks either into
     * `OR`ed 500-value branches, so a selection of hundreds of thousands stays one query.
     */
    async getBounds(ids: BoundsIds, options?: BoundsOptions): Promise<SelectionBounds | undefined> {
        const { parts = "sphere", signal } = options ?? {};
        const internal = ids.internalIds !== undefined;
        const idArr = internal ? Array.from(ids.internalIds) : Array.from(ids.publicIds);
        if (!idArr.length) {
            return undefined;
        }

        const predicate = queryPredicate(byProperty(internal ? "id" : PUBLIC_ID_PROPERTY, idArr.map(String)));
        if (!predicate) {
            return undefined;
        }

        await this.open();
        if (signal?.aborted) {
            return undefined;
        }

        const bounds = await bimIndexClient().call("bounds", { predicate, parts });
        if (!bounds || signal?.aborted) {
            return undefined;
        }
        return {
            ...(bounds.box ? { box: bounds.box } : {}),
            ...(bounds.sphere ? { sphere: bounds.sphere } : {}),
        };
    }

    /**
     * One worker call: `climb` walks the parent map to the object a picked id stands for and
     * `expand` takes that object's subtree, with neither answer leaving WASM in between —
     * the same two primitives the served backend reaches over `climb` and `selection`.
     *
     * Fused rather than concurrent as it is there: no round trip to hide, and the expansion
     * needs the climb's answer, so splitting them would only add a materialization.
     *
     * A climb landing on nothing — no object in the chain, or an index built before the
     * parent map, where `climb` answers empty for every id — falls back to legacy.
     */
    async resolveSelection(id: ObjectId, options: SelectionResolutionOptions): Promise<SelectionTarget | undefined> {
        const { mode, classAware, signal } = options;

        if (!classAware) {
            return this.resolveSelectionWithoutNodeClass(id, mode, signal);
        }

        await this.open();
        if (signal?.aborted) {
            return undefined;
        }

        const resolved = await bimIndexClient().call("resolveSelection", {
            id,
            target: objectNodeClassPredicate(),
            policy: CLIMB_POLICIES[mode],
            // A picked node that is itself an object answers for itself rather than
            // climbing past to whichever object encloses it.
            includeSelf: true,
        });
        if (signal?.aborted) {
            return undefined;
        }
        if (resolved.id === null) {
            return this.resolveSelectionWithoutNodeClass(id, mode, signal);
        }
        if (resolved.degraded) {
            // The object is right; the highlight set may cover less of its subtree than it
            // stands for, which shows up as geometry that stays unhighlighted.
            log.dataAccess.bimIndex.warn(
                `the engine hit its scan deadline resolving ${id}; the highlight set may be incomplete`,
            );
        }

        return {
            mainObjectId: resolved.id,
            // The index holds no properties, so the object comes from the scene metadata
            // if the scene has any — and `undefined` says only "not here", never "no such
            // object" (see `SelectionTarget.mainObject`).
            mainObject: await this.getObject(resolved.id),
            highlightIds: resolved.ids,
            outcome: "resolved",
        };
    }

    /** Legacy resolution, for a flag-off session and for a chain with no object in it. */
    private async resolveSelectionWithoutNodeClass(
        id: ObjectId,
        mode: SelectionResolutionMode,
        signal?: AbortSignal,
    ): Promise<SelectionTarget | undefined> {
        if (mode === "last-object") {
            return singleSelectionTarget(id);
        }
        const clicked = await this.getObject(id);
        if (!clicked || signal?.aborted) {
            return undefined;
        }
        return resolveSelectionLegacy({ access: this, clicked, mode, signal });
    }

    /** @see readObjectVisibilityStats */
    getObjectVisibilityStats(request: ObjectVisibilityStatsRequest): Promise<ObjectVisibilityStats> {
        return readObjectVisibilityStats(this.statsIndex(), request);
    }

    /** The reads `visibilityStats` makes, over the worker. */
    private statsIndex(): VisibilityStatsIndex {
        return {
            topLevelFiles: (signal) => this.topLevelFiles(signal),
            matchIdsWithDescendants: async (predicate, signal) => {
                await this.open();
                if (signal?.aborted) {
                    return undefined;
                }
                const { ids, incomplete } = await bimIndexClient().call("matchIds", {
                    predicate,
                    includeDescendants: true,
                });
                // `incomplete` rather than `degraded`, which also fires on a scan whose
                // answer is exact — refusing on that would drop the figure on any large
                // model. Refused rather than warned about: a short set would leave the
                // stats reporting a confident undercount of what the planes cut.
                if (incomplete) {
                    log.dataAccess.bimIndex.warn(
                        "the engine could not finish the scan; visibility stats cannot account for the section planes",
                    );
                    return undefined;
                }
                // The whole set as matches: splitting would mean a second scan, and this
                // backend's `filesHolding` gains nothing from it.
                return signal?.aborted ? undefined : { matches: ids, descendants: EMPTY_ID_ARRAY };
            },
            filesHolding: (files, ids, signal) => this.filesHolding(files, ids, signal),
            getDescendantIds: (object, signal) => this.getDescendantIds(object, signal),
        };
    }

    /** The top of the model tree, paged until it ends. */
    private async topLevelFiles(signal?: AbortSignal): Promise<VisibilityStatsFile[] | undefined> {
        await this.open();
        const files: VisibilityStatsFile[] = [];
        let cursor: string | null = null;
        do {
            if (signal?.aborted) {
                return undefined;
            }
            const page: Requests["modelTree"]["result"] = await bimIndexClient().call("modelTree", {
                id: null,
                limit: PAGE_SIZE,
                cursor,
            });
            for (const { id, path, descendantCount, hasChildren } of page.items) {
                files.push({ id, path, descendantCount, hasChildren });
            }
            cursor = page.next_cursor;
        } while (cursor);
        return signal?.aborted ? undefined : files;
    }

    /**
     * Which files hold any of `ids`, by subtree intersection — this engine's climb takes
     * one id per call. Subtrees are cached by {@link DescendantCache}.
     */
    private async filesHolding(
        files: readonly VisibilityStatsFile[],
        ids: SelectionSet,
        signal?: AbortSignal,
    ): Promise<Set<ObjectId> | undefined> {
        const holding = new Set<ObjectId>();
        for (const file of files) {
            if (signal?.aborted) {
                return undefined;
            }
            if (ids.has(file.id)) {
                holding.add(file.id);
            } else if (file.hasChildren) {
                const descendants = await this.getDescendantIds(fileReference(file), signal);
                if (descendants.some((descendant) => ids.has(descendant))) {
                    holding.add(file.id);
                }
            }
        }
        return signal?.aborted ? undefined : holding;
    }

    /**
     * Closes the index worker, which is the only thing that releases the OPFS files it
     * reads: it holds sync access handles on them for as long as it is alive, and a
     * locked file cannot be deleted. Terminating it is also the only way its WASM linear
     * memory goes back to the browser.
     *
     * The worker is process-wide rather than this instance's, so this closes the session
     * whether or not something else was using it — which is why disposing is the business
     * of whoever replaces the backend, not of a caller that happens to be done querying.
     */
    dispose() {
        closeBimIndex();
    }

    /**
     * A match row as the rest of the app expects one — including the bounds the engine
     * folded off `bounds.idx`, which is what a fly-to on the row frames. Everything but
     * the properties comes from the index; `loadMetaData` reaches for those through the
     * same scene metadata `getObject` reads — so a caller can open any row without
     * knowing where each half came from.
     */
    private reference({ id, path, hasChildren, bounds }: MatchRow): HierarcicalObjectReference {
        const loadObject = () => this.getObject(id);
        return {
            id,
            path,
            // `NodeType` in the API is an ambient const enum, which `isolatedModules`
            // cannot read; `types/misc` mirrors its values (see the note there).
            type: (hasChildren ? NodeType.Internal : NodeType.Leaf) as HierarcicalObjectReference["type"],
            ...(bounds ? { bounds } : {}),
            async loadMetaData(): Promise<ObjectData> {
                const object = await loadObject();
                if (!object) {
                    throw new Error(`Object ${id} has no metadata available`);
                }
                return object;
            },
        };
    }

    private open() {
        return ensureBimIndexOpen(this.openParams);
    }
}
